import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpEvent
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthenticationService } from '../_services';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from './monitoring.service';
import { SKIP_BUSY_INDICATOR, SKIP_ERROR_TOAST } from './http-context-tokens';
import { BusyService } from './services/busy.service';
import { CsrfResponse } from '../_models/authentication/csrfResponse';
import { AppErrorCode } from '../_models/app-error-code.enum';

@Injectable()
export class Interceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<CsrfResponse | null> =
    new BehaviorSubject<CsrfResponse | null>(null);
  private readonly LOGO_ID = 'ab064fd4-9b4a-4e70-972d-dafdaceb2aba';
  private technicalSupportMessage = 'please contact technical team';

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private messageService: MessageService,
    private translate: TranslateService,
    private monitoringService: MonitoringService,
    private busyService: BusyService
  ) {}

  /**
   * Intercepts HTTP requests to add credentials, CSRF tokens, and handle 401 Unauthorized errors.
   *
   * @param req The outgoing HTTP request.
   * @param next The next interceptor in the chain.
   * @returns An observable that emits the HTTP event of the handled request.
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Clone the request to add default headers.
    const clonedRequest = this.addDefaultHeaders(req);
    this.technicalSupportMessage = this.translate.instant(
      'PLEASE_CONTACT_TECHNICAL_TEAM'
    );

    // One progress signal for every write the user initiates. Counting here rather
    // than in each component means a create, update or delete is never silent,
    // whether or not its component tracks a busy flag of its own.
    const tracksBusy =
      this.isMutation(clonedRequest) &&
      !clonedRequest.context.get(SKIP_BUSY_INDICATOR);
    if (tracksBusy) {
      this.busyService.start();
    }

    return next.handle(clonedRequest).pipe(
      finalize(() => {
        if (tracksBusy) {
          this.busyService.stop();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !req.url.includes('renew-token-web')) {
          return this.refreshTokenAndRetry(clonedRequest, next);
        }

        // A rejected CSRF token means the stored token is stale relative to the
        // current session - another tab renewed, or the session rotated underneath
        // us. Renewing mints a fresh token, so recover the same way as a 401
        // instead of leaving the user unable to save anything until expiry.
        if (
          error.status === 403 &&
          !req.url.includes('renew-token-web') &&
          this.isCsrfFailure(error)
        ) {
          return this.refreshTokenAndRetry(clonedRequest, next);
        }

        if (req.context.get(SKIP_ERROR_TOAST)) {
          return throwError(error);
        }

        let correlationId =
          error.headers.get('x-request-id') ||
          error.headers.get('request-id') ||
          error.headers.get('traceparent');

        if (!correlationId) {
          const requestContext = error.headers.get('request-context');
          if (requestContext) {
            const operationIdMatch = requestContext.match(
              /operation_id=([^,;]+)/
            );
            if (operationIdMatch && operationIdMatch[1]) {
              correlationId = operationIdMatch[1];
            }
          }
        }

        const properties = {
          url: error.url,
          method: clonedRequest.method,
          status: error.status,
          statusText: error.statusText,
          correlationId: correlationId
        };

        this.monitoringService.trackException(error, properties);

        let errorTitle = this.translate.instant(
          'GENERAL_ERROR.UNEXPECTED_ERROR_TITLE',
          { status: error.status }
        );
        let errorMessage = this.translate.instant(
          'GENERAL_ERROR.UNEXPECTED_ERROR_MESSAGE',
          {
            status: error.status,
            technicalSupport: this.technicalSupportMessage
          }
        );

        switch (error.status) {
          case 0:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.NETWORK_ERROR_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.NETWORK_ERROR_MESSAGE'
            );
            break;
          case 400:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.BAD_REQUEST_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.BAD_REQUEST_MESSAGE',
              { technicalSupport: this.technicalSupportMessage }
            );
            break;
          case 403:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.FORBIDDEN_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.FORBIDDEN_MESSAGE'
            );
            break;
          case 404:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.NOT_FOUND_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.NOT_FOUND_MESSAGE',
              { technicalSupport: this.technicalSupportMessage }
            );
            break;
          case 409:
            if (error.error?.Code === AppErrorCode.ManualInUse) {
              errorTitle = this.translate.instant('ERROR');
              errorMessage = this.translate.instant('MANUAL_RETIREMENT_BLOCKED');
            }
            break;
          case 500:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.SERVER_ERROR_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.SERVER_ERROR_MESSAGE',
              { technicalSupport: this.technicalSupportMessage }
            );
            break;
          case 422:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.VALIDATION_ERROR_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.VALIDATION_ERROR_MESSAGE',
              { technicalSupport: this.technicalSupportMessage }
            );
            break;
          case 503:
            errorTitle = this.translate.instant(
              'GENERAL_ERROR.SERVICE_UNAVAILABLE_TITLE'
            );
            errorMessage = this.translate.instant(
              'GENERAL_ERROR.SERVICE_UNAVAILABLE_MESSAGE'
            );
            break;
        }

        if (error.status !== 401) {
          // 401 is handled by token refresh logic
          this.messageService.add({
            severity: 'error',
            summary: errorTitle,
            detail: errorMessage
          });
        }

        return throwError(error);
      })
    );
  }

  /**
   * Adds default headers to the request, including credentials, CSRF token (if available),
   * content type (when needed), and a fixed Logo-Id.
   * Do not add request/response body or large payloads (e.g. SJA signature base64) to headers
   * to avoid "Request headers too long" errors.
   *
   * @param req The original HTTP request.
   * @returns The cloned request with additional headers.
   */
  /** Writes the user initiated. GET and HEAD are reads and stay silent. */
  private isMutation(req: HttpRequest<any>): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
  }

  private addDefaultHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const csrfToken = this.authenticationService.getCsrfToken();
    const isFormData = req.body instanceof FormData || req.body instanceof Blob;

    const headers: { [header: string]: string } = {
      'Logo-Id': this.LOGO_ID,
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {})
    };

    return req.clone({
      withCredentials: true,
      setHeaders: headers
    });
  }

  /**
   * Determines whether a 403 was raised by the CSRF check rather than by an
   * authorization policy. The backend answers a CSRF mismatch with a text/plain
   * body, which Angular surfaces either as a raw string or wrapped in `{ text }`
   * when JSON parsing fails. A body we cannot read synchronously (e.g. a blob
   * response) falls through as a normal 403.
   *
   * @param error The error response to inspect.
   * @returns True when the response body identifies a CSRF failure.
   */
  private isCsrfFailure(error: HttpErrorResponse): boolean {
    const body = error.error;
    let text = '';

    if (typeof body === 'string') {
      text = body;
    } else if (body && typeof body.text === 'string') {
      text = body.text;
    } else if (body && typeof body.message === 'string') {
      text = body.message;
    }

    return text.toLowerCase().includes('csrf');
  }

  /**
   * Recovers from an expired access token or a stale CSRF token by renewing both,
   * then retrying the original request with the fresh CSRF token.
   * If the renewal fails, the user is logged out and the error is propagated.
   *
   * @param req The original HTTP request that was rejected.
   * @param next The next interceptor in the chain.
   * @returns An observable emitting the HTTP event of the retried request or an error.
   */
  private refreshTokenAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authenticationService.refreshToken().pipe(
        finalize(() => (this.isRefreshing = false)),
        switchMap((newToken: CsrfResponse) => {
          this.refreshTokenSubject.next(newToken);
          const updatedRequest = this.addAuthHeaders(req, newToken.CsrfToken);
          return next.handle(updatedRequest);
        }),
        catchError(err => {
          console.error('Token refresh failed, logging out.', err);
          this.authenticationService.logout();
          return throwError(() => err);
        })
      );
    } else {
      // If a token refresh is already in progress, wait until it completes
      return this.refreshTokenSubject.pipe(
        filter((token): token is CsrfResponse => token != null),
        take(1),
        switchMap((identityToken: CsrfResponse) => {
          const updatedRequest = this.addAuthHeaders(
            req,
            identityToken.CsrfToken
          );
          return next.handle(updatedRequest);
        }),
        catchError(err => {
          this.authenticationService.logout();
          return throwError(() => err);
        })
      );
    }
  }

  /**
   * Clones the request and adds authentication headers after a token refresh.
   *
   * @param req The original HTTP request.
   * @param csrfToken The new CSRF token to be added.
   * @returns The cloned request with updated headers.
   */
  private addAuthHeaders(
    req: HttpRequest<any>,
    csrfToken: string
  ): HttpRequest<any> {
    const isFormData = req.body instanceof FormData || req.body instanceof Blob;
    const authHeaders: { [header: string]: string } = {
      'Logo-Id': this.LOGO_ID,
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {})
    };

    return req.clone({
      withCredentials: true,
      setHeaders: authHeaders
    });
  }
}
